'use client'

import { useState } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { EyeIcon, EyeOffIcon } from '@/components/Icons'

export default function SignIn() {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showRecovery, setShowRecovery] = useState(false)
  const [recoveryType, setRecoveryType] = useState('password')
  const [recoveryEmail, setRecoveryEmail] = useState('')
  const [recoveryMessage, setRecoveryMessage] = useState('')
  const [isRecoveryLoading, setIsRecoveryLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  
  const router = useRouter()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await signIn('credentials', {
        identifier,
        password,
        rememberMe,
        redirect: false,
      })

      if (result?.error) {
        setError('Invalid email/username or password')
      } else if (result?.ok) {
        router.push('/problems')
      } else {
        setError('Login failed. Please try again.')
      }
    } catch (error) {
      setError('An error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRecovery = async (e) => {
    e.preventDefault()
    setIsRecoveryLoading(true)
    setRecoveryMessage('')

    try {
      const endpoint = recoveryType === 'password' 
        ? '/api/auth/forgot-password'
        : '/api/auth/forgot-username'
      
      const body = recoveryType === 'password'
        ? { identifier: recoveryEmail }
        : { email: recoveryEmail }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()
      setRecoveryMessage(data.message)
    } catch (error) {
      setRecoveryMessage('An error occurred. Please try again.')
    } finally {
      setIsRecoveryLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {showRecovery ? 'Account Recovery' : 'Sign in to your account'}
          </h2>
          {!showRecovery && (
            <p className="mt-2 text-center text-sm text-gray-300">
              Or{' '}
              <Link
                href="/auth/signup"
                className="font-medium text-primary-400 hover:text-primary-300"
              >
                create a new account
              </Link>
            </p>
          )}
        </div>

        {!showRecovery ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-900/20 border border-red-700 text-red-400 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <div className="rounded-md shadow-sm -space-y-px">
              <div className="relative">
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-dark-700 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10"
                  placeholder="Email address or username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-600 placeholder-gray-400 text-white bg-dark-700 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-600 bg-dark-700 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
                Remember me for 30 days
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => setShowRecovery(true)}
                className="text-sm text-primary-400 hover:text-primary-300"
              >
                Forgot your password or username?
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleRecovery}>
            {recoveryMessage && (
              <div className="bg-blue-900/20 border border-blue-700 text-blue-400 px-4 py-3 rounded-md">
                {recoveryMessage}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setRecoveryType('password')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md ${
                    recoveryType === 'password'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  onClick={() => setRecoveryType('username')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md ${
                    recoveryType === 'username'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Find Username
                </button>
              </div>

              <div className="relative">
                <input
                  id="recovery-email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-dark-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your email address"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isRecoveryLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isRecoveryLoading
                  ? 'Sending...'
                  : recoveryType === 'password'
                  ? 'Send Reset Link'
                  : 'Send Username'
                }
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowRecovery(false)
                  setRecoveryMessage('')
                  setRecoveryEmail('')
                }}
                className="w-full py-2 px-4 text-sm font-medium text-gray-400 hover:text-gray-300"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}